import React from 'react';
import { Link } from 'react-router-dom';
import {
  ScaleIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { usePageMeta } from '../../hooks/usePageMeta';
import Navigation from '../../components/Navigation';
import Breadcrumb from '../../components/Navigation/Breadcrumb';
import InternalLinks from '../../components/Navigation/InternalLinks';

export default function DentalConsentLawUk() {
  usePageMeta(
    'Dental consent law in the UK | GDC, CQC & Montgomery | SmileConsent',
    'How UK dental consent fits with Montgomery, GDC standards, CQC Regulation 11, and the Mental Capacity Act — and why clear, documented consent matters for practices.',
    'https://smileconsent.co.uk/dental-consent-law-uk'
  );

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Breadcrumb
          items={[
            { name: 'Home', href: '/' },
            { name: 'Consent law UK', href: '/dental-consent-law-uk' },
          ]}
        />
      </div>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-4">
        <header className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
            Dental consent law in the UK
          </h1>
          <p className="mt-4 text-lg text-gray-600 leading-relaxed">
            UK dentistry is expected to obtain <strong>valid, informed consent</strong> before treatment. The
            following frameworks shape what &ldquo;good&rdquo; consent looks like in practice — and why records
            matter if a complaint or claim arises.
          </p>
          <p className="mt-3 text-sm text-gray-500">
            This page is general information, not legal advice. For your own situation, take professional legal
            and indemnity guidance.
          </p>
        </header>

        <div className="space-y-10">
          <section className="rounded-2xl border border-gray-200 bg-slate-50/80 p-6 sm:p-8">
            <div className="flex items-start gap-3">
              <ScaleIcon className="h-8 w-8 text-blue-600 shrink-0" aria-hidden />
              <div>
                <h2 className="text-xl font-bold text-gray-900">Montgomery and material risks</h2>
                <p className="mt-3 text-gray-700 leading-relaxed">
                  The Supreme Court in <strong>Montgomery v Lanarkshire</strong> (2015) emphasised that patients
                  should be told what a reasonable person in their position would want to know — including
                  material risks, reasonable alternatives, and the option of no treatment. Consent is a
                  process of dialogue, not only a signature on a form.
                </p>
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200 p-6 sm:p-8">
            <div className="flex items-start gap-3">
              <ShieldCheckIcon className="h-8 w-8 text-emerald-600 shrink-0" aria-hidden />
              <div>
                <h2 className="text-xl font-bold text-gray-900">GDC standards</h2>
                <p className="mt-3 text-gray-700 leading-relaxed">
                  The <strong>General Dental Council</strong> expects registrants to work within its standards,
                  including obtaining valid consent, explaining options clearly, and keeping accurate records
                  of discussions and decisions. Poor documentation makes it harder to demonstrate that consent
                  was properly obtained.
                </p>
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200 p-6 sm:p-8">
            <div className="flex items-start gap-3">
              <DocumentTextIcon className="h-8 w-8 text-indigo-600 shrink-0" aria-hidden />
              <div>
                <h2 className="text-xl font-bold text-gray-900">CQC Regulation 11</h2>
                <p className="mt-3 text-gray-700 leading-relaxed">
                  In England, the <strong>Care Quality Commission</strong> assesses whether care is provided
                  with consent under <strong>Regulation 11</strong>. Practices need systems and records that
                  show consent was sought and respected, including for people who may lack capacity (see below).
                </p>
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200 p-6 sm:p-8">
            <h2 className="text-xl font-bold text-gray-900">Mental Capacity Act 2005</h2>
            <p className="mt-3 text-gray-700 leading-relaxed">
              Where capacity may be in doubt, the <strong>Mental Capacity Act 2005</strong> (England and Wales)
              sets out how to assess capacity and act in a person&apos;s best interests. Different rules apply in
              Scotland (Adults with Incapacity (Scotland) Act). Your team should follow your jurisdiction&apos;s
              framework and training.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">What this means for digital consent</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-600 shrink-0 mt-0.5" aria-hidden />
                <span>
                  Use consent processes that reflect <strong>discussion</strong> — clear information on
                  treatment, risks, alternatives, and costs — not tick-box theatre.
                </span>
              </li>
              <li className="flex gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-600 shrink-0 mt-0.5" aria-hidden />
                <span>
                  Keep <strong>contemporaneous, retrievable records</strong> (who was told what, when, and how
                  the patient decided).
                </span>
              </li>
              <li className="flex gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-600 shrink-0 mt-0.5" aria-hidden />
                <span>
                  Align templates and workflows with your <strong>indemnifier</strong> and local policies.
                </span>
              </li>
            </ul>
          </section>
        </div>

        <div className="mt-14 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white p-8 sm:p-10">
          <h2 className="text-xl font-bold">SmileConsent for UK practices</h2>
          <p className="mt-2 text-blue-100 leading-relaxed">
            AI-assisted, UK-law-aware consent wording, multi-treatment support, and digital signing — with
            records you can stand behind.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-6 py-3 font-semibold text-blue-700 hover:bg-blue-50 transition-colors"
            >
              Start free trial
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
            <Link
              to="/book-demo"
              className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/80 px-6 py-3 font-semibold text-white hover:bg-white/10 transition-colors"
            >
              Book a demo
            </Link>
          </div>
          <p className="mt-6 text-sm text-blue-200">
            Deeper reading:{' '}
            <Link to="/cpd-legal-consent" className="underline hover:text-white">
              free CPD on legal consent in dentistry
            </Link>
            {' · '}
            <Link to="/insights/consent-is-coming" className="underline hover:text-white">
              regulation &amp; consent insights
            </Link>
            .
          </p>
        </div>

        <div className="mt-12">
          <InternalLinks
            title="Related compliance resources"
            links={[
              {
                title: 'NHS mixed treatment documentation',
                description: 'Mixed NHS and private treatment consent, FP17DC principles, and GDC 2.2',
                path: '/nhs-mixed-treatment-compliance',
              },
              {
                title: 'Tooth extraction consent form UK',
                description: 'Example surgical and routine extraction consent page',
                path: '/tooth-extraction-consent-form-uk',
              },
              {
                title: 'Root canal consent form UK',
                description: 'Endodontic consent with alternatives and long-term prognosis',
                path: '/root-canal-consent-form-uk',
              },
              {
                title: 'Composite bonding consent form UK',
                description: 'Cosmetic consent wording for limitations and maintenance',
                path: '/composite-bonding-consent-form-uk',
              },
              {
                title: 'Veneer consent form UK',
                description: 'Consent records for cosmetic expectations and tooth preparation',
                path: '/veneer-consent-form-uk',
              },
            ]}
          />
        </div>
      </article>
    </div>
  );
}
